import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  catchError,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
  throwError,
} from "rxjs";
import { NoShowToastContext } from "src/app/core/interceptors/http-toast.interceptor";
import { ToastService } from "src/app/shared/services/toast.service";
import { environment } from "src/environments/environment";
import {
  BILLING_INVOICE_DEFAULT_PAGE_SIZE,
  BillingCustomer,
  BillingData,
  BillingInvoiceDTO,
  BillingResolverData,
  BillingSubscriptionDTO,
  CreateBillingCustomerDTO,
  CreateSubscriptionDTO,
  InvoiceHistoryDTO,
  isCustomerBillingActive,
  PaymentMethodDTO,
  ReverseSubscriptionCancellationResponse,
  SubscriptionCreateResponse,
  SubscriptionPlanDTO,
  SubscriptionRetryPaymentResponse,
  SubscriptionStatus,
  UpdateBillingCustomerDTO,
} from "./billing.model";
import { StripeLoaderService } from "./stripe-loader.service";

const ACTIVE_BILLING_STATUSES = new Set<string>([
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.TRIALING,
]);

@Injectable({
  providedIn: "root",
})
export class BillingService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly stripeLoader = inject(StripeLoaderService);
  private readonly baseUrl = `${environment.baseApiUrl}/billing`;

  private readonly billingResolverData = signal<BillingResolverData | null>(
    null,
  );

  /** True after `/billing` loaded PM, subscription, plans, and invoices at least once (snapshot only). */
  private billingDetailsLoaded = false;

  private customerLoad$: Observable<BillingCustomer | null> | null = null;

  private readonly subscriptionSyncedSubject = new Subject<{
    subscription: BillingSubscriptionDTO | null;
    customer: BillingCustomer | null;
  }>();

  clearCache(): void {
    this.billingResolverData.set(null);
    this.billingDetailsLoaded = false;
    this.customerLoad$ = null;
  }

  setBillingResolverData(data: BillingResolverData): void {
    this.billingResolverData.set(data);
  }

  markBillingDetailsLoaded(): void {
    this.billingDetailsLoaded = true;
  }

  hasBillingDetailsCached(): boolean {
    return this.billingDetailsLoaded;
  }

  getBillingResolverData(): BillingResolverData | null {
    return this.billingResolverData();
  }

  getPreloadedBilling(): BillingData | null {
    return this.billingResolverData()?.billing ?? null;
  }

  getPreloadedSubscriptionPlans(): SubscriptionPlanDTO[] {
    return this.billingResolverData()?.plans ?? [];
  }

  updatePreloadedBilling(partial: Partial<BillingData>): void {
    const current = this.billingResolverData();
    if (!current?.billing) {
      return;
    }

    this.billingResolverData.set({
      ...current,
      billing: {
        ...current.billing,
        ...partial,
      },
    });
  }

  /**
   * Notifies open views (e.g. `/billing`) after subscription-changing mutations
   * initiated outside {@link BillingComponent} (usage widgets, usage overview).
   */
  subscriptionSynced$(): Observable<{
    subscription: BillingSubscriptionDTO | null;
    customer: BillingCustomer | null;
  }> {
    return this.subscriptionSyncedSubject.asObservable();
  }

  /** GET /billing/subscription, merge cache, and notify {@link subscriptionSynced$} listeners. */
  refreshSubscriptionAndNotifyListeners(): Observable<{
    subscription: BillingSubscriptionDTO | null;
    customer: BillingCustomer | null;
  }> {
    return this.getSubscription().pipe(
      map((subscription) => {
        const customer = this.getPreloadedBilling()?.customer ?? null;
        this.updatePreloadedBilling({ subscription, customer });
        const payload = { subscription, customer };
        this.subscriptionSyncedSubject.next(payload);
        return payload;
      }),
    );
  }

  /**
   * Bootstrap billing cache with GET /billing/customer only (initial data / guards).
   * PM, subscription, plans, and invoices load only via {@link loadBillingPageData} on `/billing`.
   */
  ensureBillingCached(): Observable<BillingData> {
    const cached = this.getPreloadedBilling();
    if (cached) {
      return of(cached);
    }

    return this.getCustomerCachedOrLoad().pipe(
      map((customer) => {
        const data: BillingData = {
          customer,
          paymentMethods: [],
          subscription: null,
          invoices: null,
        };

        const current = this.billingResolverData();
        this.setBillingResolverData({
          billing: data,
          plans: current?.plans ?? [],
        });

        return data;
      }),
    );
  }

  /**
   * Uncached load for `/billing` (resolver + fallback). Always calls:
   * customer, payment methods, subscription, subscription-plans, and first invoice page.
   * Updates {@link billingResolverData} from API responses.
   */
  loadBillingPageData(): Observable<BillingResolverData> {
    return forkJoin({
      customer: this.getCustomer(),
      paymentMethods: this.getPaymentMethods(),
      subscription: this.getSubscription(),
      plans: this.getSubscriptionPlans().pipe(
        catchError((error) => {
          console.error("Error fetching subscription plans:", error);
          return of([] as SubscriptionPlanDTO[]);
        }),
      ),
    }).pipe(
      switchMap(({ customer, paymentMethods, subscription, plans }) => {
        const baseBilling: BillingData = {
          customer,
          paymentMethods,
          subscription,
          invoices: null,
        };

        const commit = (billing: BillingData): BillingResolverData => {
          const resolverData: BillingResolverData = { billing, plans };
          this.setBillingResolverData(resolverData);
          this.markBillingDetailsLoaded();
          return resolverData;
        };

        if (!customer) {
          return of(commit(baseBilling));
        }

        return this.getInvoices({
          limit: String(BILLING_INVOICE_DEFAULT_PAGE_SIZE),
        }).pipe(
          map((invoices) =>
            commit({
              ...baseBilling,
              invoices,
            }),
          ),
          catchError(() => of(commit(baseBilling))),
        );
      }),
    );
  }

  /** Evaluates active billing from cache only (no HTTP). Uses customer bootstrap fields. */
  hasActiveBillingCached(): boolean {
    const billing = this.getPreloadedBilling();
    if (!billing?.customer) {
      return false;
    }

    if (isCustomerBillingActive(billing.customer)) {
      return true;
    }

    if (!this.billingDetailsLoaded) {
      return false;
    }

    return (
      billing.paymentMethods.length > 0 &&
      !!billing.subscription &&
      ACTIVE_BILLING_STATUSES.has(billing.subscription.status)
    );
  }

  /** Reads billing cache first; use {@link getCustomer} after mutations to refresh cache. */
  getCustomerCachedOrLoad(): Observable<BillingCustomer | null> {
    const preloaded = this.getPreloadedBilling();
    if (preloaded !== null) {
      return of(preloaded.customer);
    }

    if (!this.customerLoad$) {
      this.customerLoad$ = this.getCustomer().pipe(
        tap((customer) => this.mergeCustomerIntoBillingCache(customer)),
        shareReplay(1),
        finalize(() => {
          this.customerLoad$ = null;
        }),
      );
    }

    return this.customerLoad$;
  }

  private mergeCustomerIntoBillingCache(customer: BillingCustomer | null): void {
    const current = this.getPreloadedBilling();
    const data: BillingData = {
      customer,
      paymentMethods: current?.paymentMethods ?? [],
      subscription: current?.subscription ?? null,
      invoices: current?.invoices ?? null,
    };

    this.setBillingResolverData({
      billing: data,
      plans: this.getPreloadedSubscriptionPlans(),
    });
  }

  /** Uncached GET — use after create/update customer to refresh {@link billingResolverData}. */
  getCustomer(): Observable<BillingCustomer | null> {
    return this.http
      .get<BillingCustomer>(`${this.baseUrl}/customer`, {
        ...NoShowToastContext(),
      })
      .pipe(
        catchError((error) => {
          if (error?.status === 404) {
            return of(null);
          }
          console.error("Error fetching billing customer:", error);
          return of(null);
        }),
      );
  }

  createCustomer(
    payload: CreateBillingCustomerDTO,
  ): Observable<BillingCustomer> {
    return this.http
      .post<{
        customer: BillingCustomer;
      }>(`${this.baseUrl}/customer`, payload)
      .pipe(
        map((response) => response.customer),
        switchMap((customer) =>
          this.refreshCustomerBootstrapCache(customer).pipe(
            map((fresh) => fresh ?? customer),
          ),
        ),
        catchError((error) => {
          this.toast.error(
            this.translate.instant("toast_errors.registerBillingFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  updateCustomer(
    payload: UpdateBillingCustomerDTO,
  ): Observable<BillingCustomer> {
    return this.http
      .put<{
        customer: BillingCustomer;
      }>(`${this.baseUrl}/customer`, payload)
      .pipe(
        map((response) => response.customer),
        switchMap((customer) =>
          this.refreshCustomerBootstrapCache(customer).pipe(
            map((fresh) => fresh ?? customer),
          ),
        ),
        catchError((error) => {
          this.toast.error(
            this.translate.instant("toast_errors.updateBillingFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  /**
   * GET /billing/customer after billing mutations — keeps bootstrap cache in sync (guards, subscription fields on customer).
   */
  refreshCustomerBootstrapCache(
    fallback: BillingCustomer | null = this.getPreloadedBilling()?.customer ??
      null,
  ): Observable<BillingCustomer | null> {
    return this.getCustomer().pipe(
      map((fresh) => fresh ?? fallback),
      tap((customer) => this.mergeCustomerIntoBillingCache(customer)),
    );
  }

  private afterBillingMutationRefreshCustomer<T>(
    result: T,
  ): Observable<T> {
    return this.refreshCustomerBootstrapCache().pipe(map(() => result));
  }

  getPaymentMethods(): Observable<PaymentMethodDTO[]> {
    return this.http
      .get<PaymentMethodDTO[]>(`${this.baseUrl}/payment-method`, {
        ...NoShowToastContext(),
      })
      .pipe(
        catchError((error) => {
          if (error?.status === 404) {
            return of([]);
          }
          console.error("Error fetching payment methods:", error);
          return of([]);
        }),
      );
  }

  createPaymentMethod(paymentMethodId: string): Observable<PaymentMethodDTO> {
    return this.http
      .post<{
        paymentMethod: PaymentMethodDTO;
      }>(`${this.baseUrl}/payment-method`, { paymentMethodId })
      .pipe(
        map((response) => response.paymentMethod),
        switchMap((paymentMethod) =>
          this.afterBillingMutationRefreshCustomer(paymentMethod),
        ),
        catchError((error) => {
          this.toast.error(
            this.translate.instant("toast_errors.createPaymentMethodFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  updatePaymentMethod(paymentMethodId: string): Observable<PaymentMethodDTO> {
    return this.http
      .put<{
        paymentMethod: PaymentMethodDTO;
      }>(`${this.baseUrl}/payment-method`, { paymentMethodId })
      .pipe(
        map((response) => response.paymentMethod),
        switchMap((paymentMethod) =>
          this.afterBillingMutationRefreshCustomer(paymentMethod),
        ),
        catchError((error) => {
          this.toast.error(
            this.translate.instant("toast_errors.updatePaymentMethodFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  deletePaymentMethod(paymentMethodId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/payment-method`, {
        body: { paymentMethodId },
      })
      .pipe(
        switchMap(() =>
          this.refreshCustomerBootstrapCache().pipe(map(() => undefined)),
        ),
        catchError((error) => {
          this.toast.error(
            this.translate.instant("toast_errors.deletePaymentMethodFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  getSubscriptionPlans(): Observable<SubscriptionPlanDTO[]> {
    return this.http
      .get<SubscriptionPlanDTO[]>(`${this.baseUrl}/subscription-plans`)
      .pipe(
        catchError((error) => {
          this.toast.error(
            this.translate.instant("toast_errors.getSubscriptionPlansFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  getSubscription(): Observable<BillingSubscriptionDTO | null> {
    return this.http
      .get<BillingSubscriptionDTO>(`${this.baseUrl}/subscription`, {
        ...NoShowToastContext(),
      })
      .pipe(
        catchError((error) => {
          if (error?.status === 404) {
            return of(null);
          }
          console.error("Error fetching subscription:", error);
          return of(null);
        }),
      );
  }

  createSubscription(
    promotionCode?: string,
  ): Observable<SubscriptionCreateResponse> {
    const body: CreateSubscriptionDTO = {};
    const trimmedPromotionCode = promotionCode?.trim();

    if (trimmedPromotionCode) {
      body.promotionCode = trimmedPromotionCode;
    }

    return this.http
      .post<SubscriptionCreateResponse>(`${this.baseUrl}/subscription`, body)
      .pipe(
        switchMap((response) =>
          from(
            this.confirmSubscriptionPayment(response.subscription.clientSecret),
          ).pipe(
            map(() => response),
            switchMap((confirmed) =>
              this.afterBillingMutationRefreshCustomer(confirmed),
            ),
          ),
        ),
        catchError((error) => {
          if (this.shouldPropagateSubscriptionError(error)) {
            return throwError(() => error);
          }
          this.toast.error(
            this.translate.instant("toast_errors.createSubscriptionFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  updateSubscription(newPriceId: string): Observable<BillingSubscriptionDTO> {
    return this.http
      .put<{
        subscription: BillingSubscriptionDTO;
      }>(`${this.baseUrl}/subscription`, { newPriceId })
      .pipe(
        map((response) => response.subscription),
        switchMap((subscription) =>
          this.afterBillingMutationRefreshCustomer(subscription),
        ),
        catchError((error) => {
          this.toast.error(
            this.translate.instant("toast_errors.updateSubscriptionPlanFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  deleteSubscription(): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/subscription`).pipe(
      switchMap(() =>
        this.refreshCustomerBootstrapCache().pipe(map(() => undefined)),
      ),
      catchError((error) => {
        this.toast.error(
          this.translate.instant("toast_errors.deleteSubscriptionFailed"),
        );
        return throwError(() => error);
      }),
    );
  }

  reverseSubscriptionCancellation(): Observable<ReverseSubscriptionCancellationResponse> {
    return this.http
      .post<ReverseSubscriptionCancellationResponse>(
        `${this.baseUrl}/subscription/reverse-cancellation`,
        {},
      )
      .pipe(
        switchMap((response) =>
          this.afterBillingMutationRefreshCustomer(response),
        ),
        catchError((error) => {
          this.toast.error(
            this.translate.instant(
              "toast_errors.reverseSubscriptionCancellationFailed",
            ),
          );
          return throwError(() => error);
        }),
      );
  }

  applyPromotion(promotionCode: string): Observable<BillingSubscriptionDTO> {
    return this.http
      .post<{
        message: string;
        subscription: BillingSubscriptionDTO;
      }>(
        `${this.baseUrl}/subscription/promotion`,
        { promotionCode },
        NoShowToastContext(),
      )
      .pipe(
        switchMap((response) => {
          const clientSecret = response.subscription.clientSecret;
          if (!clientSecret) {
            return of(response.subscription);
          }

          return from(this.confirmSubscriptionPayment(clientSecret)).pipe(
            map(() => response.subscription),
          );
        }),
        switchMap((subscription) =>
          this.afterBillingMutationRefreshCustomer(subscription),
        ),
      );
  }

  retrySubscriptionPayment(
    paymentMethodId?: string,
  ): Observable<SubscriptionRetryPaymentResponse> {
    return this.http
      .post<SubscriptionRetryPaymentResponse>(
        `${this.baseUrl}/subscription-retry-payment`,
        {},
        NoShowToastContext(),
      )
      .pipe(
        switchMap((response) =>
          from(
            this.confirmSubscriptionPayment(
              response.clientSecret,
              paymentMethodId,
            ),
          ).pipe(
            map(() => response),
            switchMap((confirmed) =>
              this.afterBillingMutationRefreshCustomer(confirmed),
            ),
          ),
        ),
        catchError((error) => {
          if (this.shouldPropagateSubscriptionError(error)) {
            return throwError(() => error);
          }
          this.toast.error(
            this.translate.instant("toast_errors.createSubscriptionFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  getInvoices(
    params: { limit?: string; cursor?: string } = {},
  ): Observable<InvoiceHistoryDTO> {
    return this.http
      .get<InvoiceHistoryDTO>(`${this.baseUrl}/invoice`, {
        params,
        ...NoShowToastContext(),
      })
      .pipe(
        catchError((error) => {
          // No billing profile yet ("Billing profile not found"): empty history, not an error.
          if (error?.status === 404) {
            return of<InvoiceHistoryDTO>({
              items: [],
              cursor: null,
              limit: Number(params.limit ?? 0),
            });
          }
          this.toast.error(
            this.translate.instant("toast_errors.getInvoicesHistoryFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  getInvoiceDetail(invoiceId: string): Observable<BillingInvoiceDTO> {
    return this.http
      .get<BillingInvoiceDTO>(`${this.baseUrl}/invoice/${invoiceId}`)
      .pipe(
        catchError((error) => {
          this.toast.error(
            this.translate.instant(
              "toast_errors.getInvoicesHistoryDetailFailed",
            ),
          );
          return throwError(() => error);
        }),
      );
  }

  getUpcomingInvoice(): Observable<BillingInvoiceDTO | null> {
    return this.http
      .get<BillingInvoiceDTO>(`${this.baseUrl}/invoice-upcoming`, {
        ...NoShowToastContext(),
      })
      .pipe(
        catchError((error) => {
          if (error?.status === 404) {
            return of(null);
          }
          this.toast.error(
            this.translate.instant("toast_errors.getInvoiceUpcomingFailed"),
          );
          return throwError(() => error);
        }),
      );
  }

  downloadInvoice(invoiceId: string): Observable<unknown> {
    return this.http.get(`${this.baseUrl}/invoice/${invoiceId}/download`).pipe(
      catchError((error) => {
        this.toast.error(
          this.translate.instant("toast_errors.downloadInvoiceFailed"),
        );
        return throwError(() => error);
      }),
    );
  }

  private isStripeClientError(error: unknown): boolean {
    return !!(
      error &&
      typeof error === "object" &&
      "type" in error &&
      typeof (error as { type?: string }).type === "string"
    );
  }

  private shouldPropagateSubscriptionError(error: unknown): boolean {
    if (this.isStripeClientError(error)) {
      return true;
    }
    const status = (error as { status?: number })?.status;
    return status === 400 || status === 422;
  }

  private async confirmSubscriptionPayment(
    clientSecret: string,
    paymentMethodId?: string,
  ): Promise<void> {
    if (!clientSecret) {
      return;
    }

    const stripe = await this.stripeLoader.load();
    const confirmOptions: { payment_method?: string } = {};
    if (paymentMethodId) {
      confirmOptions.payment_method = paymentMethodId;
    }

    const result = await stripe?.confirmCardPayment(
      clientSecret,
      confirmOptions,
    );

    if (result?.error) {
      console.error(result.error.message);
      throw result.error;
    }

    const paymentStatus = result?.paymentIntent?.status;
    if (paymentStatus && paymentStatus !== "succeeded") {
      const message =
        result.paymentIntent.last_payment_error?.message ??
        this.translate.instant("billingPage.activate.paymentFailedDefault");
      throw { type: "card_error", message };
    }

    if (paymentStatus === "succeeded") {
      this.toast.success(
        this.translate.instant("messages.subscription_confirmed"),
        undefined,
        2000,
      );
    }
  }
}
